import type { FC, MouseEvent, ReactNode } from 'react'
import type { Product } from '@/types'

import { useEffect, useRef, useState } from 'react'
import { Check, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatPrice, productCmsKey } from '@/lib/format'
import { useGlassSheen } from '@/hooks/use-glass-sheen'
import { useBag } from '@/components/generals/bag-provider'
import { ProductImage } from '@/components/generals/product-image'

interface ProductCardProps {
    product: Product
    onView: (product: Product) => void
}

export const ProductCard: FC<ProductCardProps> = ({ product, onView }): ReactNode => {
    const { addItem } = useBag()
    const [added, setAdded] = useState(false)
    const timer = useRef<number | null>(null)
    const sheen = useGlassSheen()

    useEffect(() => () => {
        if (timer.current) window.clearTimeout(timer.current)
    }, [])

    const handleAdd = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        addItem(product)
        setAdded(true)

        if (timer.current) window.clearTimeout(timer.current)
        timer.current = window.setTimeout(() => setAdded(false), 1800)
    }

    // Clicking anywhere on the card opens the details. The full-size button below gives
    // keyboard and screen reader users the same action; its click bubbles up to here.
    return (
        <article
            onClick={() => onView(product)}
            onPointerMove={sheen.onPointerMove}
            onPointerLeave={sheen.onPointerLeave}
            className='group relative aspect-[4/5] cursor-pointer overflow-hidden rounded-[2rem] bg-sand shadow-[0_30px_60px_-36px_rgba(34,28,22,0.5)] ring-1 ring-espresso/5'
        >
            <ProductImage
                src={product.image}
                alt={product.image_alt ?? product.name}
                sizes='(min-width: 1024px) 30vw, (min-width: 640px) 46vw, 92vw'
                className='transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]'
            />

            <div aria-hidden='true' className='pointer-events-none absolute inset-0 bg-linear-to-t from-espresso/35 via-transparent to-transparent' />

            <button
                type='button'
                aria-label={`View the details of ${product.name}`}
                className='absolute inset-0 rounded-[2rem] focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-gold-deep'
            />

            {product.edition && (
                <p className='glass glass-clear absolute left-4 top-4 rounded-full px-3.5 py-1.5 text-[0.62rem] uppercase tracking-[0.2em] text-espresso'>
                    <span data-mdlfy-cms={productCmsKey(product, 'edition')}>
                        {product.edition}
                    </span>
                </p>
            )}

            <div
                ref={sheen.ref}
                className='glass glass-frost absolute inset-x-3 bottom-3 rounded-[1.6rem] p-4 text-espresso transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:-translate-y-1 sm:p-5'
            >
                <div className='flex items-start justify-between gap-3'>
                    <div className='min-w-0'>
                        <p
                            data-mdlfy-cms={productCmsKey(product, 'category')}
                            className='text-[0.62rem] uppercase tracking-[0.22em] text-espresso/60'
                        >
                            {product.category}
                        </p>

                        <h3
                            data-mdlfy-cms={productCmsKey(product, 'name')}
                            className='mt-1 font-heading text-[1.65rem] font-normal leading-tight'
                        >
                            {product.name}
                        </h3>
                    </div>

                    <p className='shrink-0 pt-4 font-heading text-xl'>
                        {formatPrice(product.price)}
                    </p>
                </div>

                <div className='mt-3 flex items-center justify-between gap-3'>
                    {product.material ? (
                        <p
                            data-mdlfy-cms={productCmsKey(product, 'material')}
                            className='min-w-0 truncate text-xs text-espresso/70'
                        >
                            {product.material}
                        </p>
                    ) : (
                        <span />
                    )}

                    <button
                        type='button'
                        onClick={handleAdd}
                        aria-label={`Add ${product.name} to your bag`}
                        className={cn(
                            'flex size-10 shrink-0 items-center justify-center rounded-full transition-all duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-deep',
                            added ? 'bg-gold-deep text-ivory' : 'bg-espresso text-ivory hover:scale-105 hover:bg-ink'
                        )}
                    >
                        {added ? <Check className='size-4' strokeWidth={1.75} /> : <Plus className='size-4' strokeWidth={1.5} />}
                    </button>
                </div>
            </div>
        </article>
    )
}
